import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axiosInstance from "../../api/axiosInstance";

const initialState = {
  statements: [],
  total: 0,
  limit: 10,
  page: 1,
  loading: false,
  error: null,
  diamondSettledBets: {
    rows: [],
    metadata: [],
    loading: false,
    error: null,
  },
};

function extractDiamondSettledPayload(res) {
  if (!res) return { data: [], metadata: [] };
  if (Array.isArray(res)) {
    const group = res[0] || {};
    return {
      data: Array.isArray(group.data) ? group.data : [],
      metadata: Array.isArray(group.metadata) ? group.metadata : [],
    };
  }
  if (Array.isArray(res.data)) {
    const group = res.data[0] || {};
    return {
      data: Array.isArray(group.data) ? group.data : [],
      metadata: Array.isArray(group.metadata) ? group.metadata : [],
    };
  }
  return { data: [], metadata: [] };
}

export const fetchDiamondSettledBetsAsync = createAsyncThunk(
  "statement/fetchDiamondSettledBets",
  async ({ match_id, market_id }, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.post("/bet/diamondSettledBets", {
        match_id: String(match_id),
        search: {
          market_id: String(market_id),
          delete_status: [0],
        },
      });
      if (res && typeof res === "object" && res.status === false) {
        return rejectWithValue(res.message || "Failed to fetch settled bets");
      }
      return extractDiamondSettledPayload(res);
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Failed to fetch settled bets",
      );
    }
  },
);

export const fetchStatementsAsync = createAsyncThunk(
  "statement/fetchStatements",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        "/account/statements",
        payload || {},
      );
      if (!response?.status) {
        return rejectWithValue(
          response?.message || "Failed to fetch statements",
        );
      }
      return { response, payload };
    } catch (error) {
      return rejectWithValue(
        error.response?.message || "Failed to fetch statements",
      );
    }
  },
);

const statementSlice = createSlice({
  name: "statement",
  initialState,
  reducers: {
    clearStatements: (state) => {
      state.statements = [];
      state.total = 0;
      state.limit = 10;
      state.page = 1;
      state.error = null;
    },
    clearDiamondSettledBets: (state) => {
      state.diamondSettledBets = {
        rows: [],
        metadata: [],
        loading: false,
        error: null,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchStatementsAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchStatementsAsync.fulfilled, (state, action) => {
        state.loading = false;
        const { response } = action.payload;

        const dataGroup = response?.data?.[0];
        const rawStatements = dataGroup?.data || [];
        const metadata = dataGroup?.metadata?.[0] || {};

        state.statements = rawStatements.map((item) => ({
          ...item,
          cr: item.credit_debit > 0 ? item.credit_debit : 0,
          dr: item.credit_debit < 0 ? Math.abs(item.credit_debit) : 0,
          prev_balance: item.balance - item.credit_debit,
          comm: item.bonus || 0,
        }));

        state.total = metadata.total || 0;
        state.limit = metadata.limit || state.limit;
        state.page = metadata.page || 1;
        state.error = null;
      })
      .addCase(fetchStatementsAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchDiamondSettledBetsAsync.pending, (state) => {
        state.diamondSettledBets.loading = true;
        state.diamondSettledBets.error = null;
      })
      .addCase(fetchDiamondSettledBetsAsync.fulfilled, (state, action) => {
        state.diamondSettledBets.loading = false;
        state.diamondSettledBets.rows = action.payload.data || [];
        state.diamondSettledBets.metadata = action.payload.metadata || [];
        state.diamondSettledBets.error = null;
      })
      .addCase(fetchDiamondSettledBetsAsync.rejected, (state, action) => {
        state.diamondSettledBets.loading = false;
        state.diamondSettledBets.error = action.payload;
      });
  },
});

export const { clearStatements, clearDiamondSettledBets } =
  statementSlice.actions;
export default statementSlice.reducer;
